import { ref, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'
import { useSnackbarStore } from '@/stores/snackbar'
import { useI18n } from 'vue-i18n'
import api from '@/utils/api'

export const useSessionTimeout = () => {
  const authStore = useAuthStore()
  const router = useRouter()
  const snackbar = useSnackbarStore()
  const { t } = useI18n()
  
  // Session timeout settings (in milliseconds)
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000 // 30 минут бездействия
  const WARNING_TIME = 5 * 60 * 1000 // Предупреждение за 5 минут до окончания
  const TOKEN_CHECK_INTERVAL = 60 * 1000 // Проверка токена каждую минуту
  
  const lastActivity = ref(Date.now())
  const isWarningShown = ref(false)
  const activityTimer = ref(null)
  const tokenCheckTimer = ref(null)
  const warningTimer = ref(null)
  
  // Events that indicate user activity
  const ACTIVITY_EVENTS = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click'
  ]
  
  // Update last activity time
  const updateActivity = () => {
    lastActivity.value = Date.now()
    isWarningShown.value = false
    
    // Clear existing warning timer
    if (warningTimer.value) {
      clearTimeout(warningTimer.value)
      warningTimer.value = null
    }
    
    // Set new warning timer
    warningTimer.value = setTimeout(() => {
      showSessionWarning()
    }, INACTIVITY_TIMEOUT - WARNING_TIME)
  }
  
  // Show session expiry warning
  const showSessionWarning = () => {
    if (!authStore.isAuthenticated || isWarningShown.value) return
    
    isWarningShown.value = true
    
    snackbar.show({
      message: t('session.warningMessage'),
      type: 'warning',
      timeout: WARNING_TIME,
      action: {
        text: t('session.extendSession'),
        color: 'warning',
        onClick: () => {
          extendSession()
        }
      }
    })
    
    // Auto logout after warning period
    setTimeout(() => {
      if (isWarningShown.value && authStore.isAuthenticated) {
        handleSessionTimeout()
      }
    }, WARNING_TIME)
  }
  
  // Extend session by updating activity and calling backend
  const extendSession = async () => {
    try {
      await api.post('/session/extend')
      updateActivity()
      snackbar.success(t('session.sessionExtended'))
    } catch (error) {
      // Console log removed
      snackbar.error(t('session.loginRequired'))
      handleSessionTimeout()
    }
  }
  
  // Handle session timeout
  const handleSessionTimeout = () => {
    // Console log removed
    
    // Clear all timers
    clearAllTimers()
    
    // Show timeout message
    snackbar.error(t('session.timeoutMessage'))
    
    // Logout user
    authStore.logout()
    
    // Redirect to login
    router.push('/login?reason=timeout')
  }
  
  // Check if JWT token is expired
  const checkTokenExpiry = async () => {
    if (!authStore.token) return
    
    try {
      // Decode JWT to check expiry (simple check without verification)
      const tokenParts = authStore.token.split('.')
      if (tokenParts.length !== 3) return
      
      const payload = JSON.parse(atob(tokenParts[1]))
      const currentTime = Math.floor(Date.now() / 1000)
      
      // If token expires within 5 minutes, try to refresh or logout
      if (payload.exp && payload.exp - currentTime < 300) { // 5 minutes
        // Console log removed
        
        const isValid = await authStore.verifyToken()
        if (!isValid) {
          // Console log removed
          handleSessionTimeout()
        }
      }
    } catch (error) {
      // Console log removed
      // If we can't parse token, it might be invalid
      const isValid = await authStore.verifyToken()
      if (!isValid) {
        handleSessionTimeout()
      }
    }
  }
  
  // Clear all session timers
  const clearAllTimers = () => {
    if (activityTimer.value) {
      clearInterval(activityTimer.value)
      activityTimer.value = null
    }
    
    if (tokenCheckTimer.value) {
      clearInterval(tokenCheckTimer.value)
      tokenCheckTimer.value = null
    }
    
    if (warningTimer.value) {
      clearTimeout(warningTimer.value)
      warningTimer.value = null
    }
  }
  
  // Start session monitoring
  const startSessionMonitoring = () => {
    if (!authStore.isAuthenticated) return
    
    // Initialize activity tracking
    updateActivity()
    
    // Add activity listeners
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, updateActivity, true)
    })
    
    // Check for inactivity every minute
    activityTimer.value = setInterval(() => {
      const inactiveTime = Date.now() - lastActivity.value
      
      if (inactiveTime >= INACTIVITY_TIMEOUT && !isWarningShown.value) {
        handleSessionTimeout()
      }
    }, 60000) // Check every minute
    
    // Check token expiry regularly
    tokenCheckTimer.value = setInterval(() => {
      checkTokenExpiry()
    }, TOKEN_CHECK_INTERVAL)
  }
  
  // Stop session monitoring
  const stopSessionMonitoring = () => {
    // Console log removed
    
    // Remove activity listeners
    ACTIVITY_EVENTS.forEach(event => {
      document.removeEventListener(event, updateActivity, true)
    })
    
    // Clear all timers
    clearAllTimers()
    
    // Reset state
    isWarningShown.value = false
  }
  
  // Initialize on component mount
  onMounted(() => {
    if (authStore.isAuthenticated) {
      startSessionMonitoring()
    }
  })
  
  // Cleanup on component unmount
  onUnmounted(() => {
    stopSessionMonitoring()
  })
  
  // Watch for auth changes
  const watchAuthChanges = () => {
    authStore.$subscribe((mutation, state) => {
      if (state.isAuthenticated && !activityTimer.value) {
        startSessionMonitoring()
      } else if (!state.isAuthenticated && activityTimer.value) {
        stopSessionMonitoring()
      }
    })
  }
  
  return {
    startSessionMonitoring,
    stopSessionMonitoring,
    extendSession,
    updateActivity,
    watchAuthChanges,
    lastActivity: ref(lastActivity),
    isWarningShown: ref(isWarningShown)
  }
}